import { Controller } from "@hotwired/stimulus"
import { showNotification } from "./../helpers"

// Targety outletu invoice, z których liczy się data kursu (Invoice#get_date_for_exchange).
const EXCHANGE_DATE_TARGETS = ["issueDate", "sellDate", "paidDate"]
// Wpisywanie daty z klawiatury emituje "change" po każdym poprawnym dniu — bez zwłoki
// każde przejście przez pole kosztowałoby zapytanie o kurs.
const PREVIEW_REFRESH_DELAY_MS = 300

// Sekcja "Kurs waluty" na formatce faktury (TW desktop + mobile). Trzyma całą sekcję: pola kursu,
// checkbox "pokaż przeliczenia na wizualizacji" i notyfikację "waluta spoza banku". Główną walutę
// faktury i daty (osobne partiale) czyta przez outlet `invoice`.
export default class extends Controller {
  static targets = ["exchangeKind", "exchangeCurrency", "exchangeKindWrapper", "exchangeCurrencyRate",
    "exchangeCurrencyRateLabel", "exchangeNote", "exchangeRateWrapper", "exchangeNoteWrapper",
    "exchangeDisplayWrapper", "exchangeDisplayCheckbox"]

  static values = {
    previewUrl: String,
    invoiceKind: String,
    baseInvoiceId: String,
    rateUnavailable: String,
    rateFetchFailed: String,
    accountCurrency: String,
    exchangeRatesAvailableForBanks: Object,
    currencyNotInBank: String,
  }

  static outlets = ["invoice"]

  initialize() {
    this.previewCache = new Map()
  }

  connect() {
    this.handledExchangeCurrency = this.exchangeCurrencyTarget.value
  }

  disconnect() {
    clearTimeout(this.previewRefreshTimer)
  }

  // Waluta faktury i daty żyją poza sekcją kursu (osobne partiale formatki) — czytamy je przez outlet
  // invoice, a o zmianie waluty dowiadujemy się z broadcastu `currency-changed` (currency#update),
  // podpiętego deklaratywnie w HTML. Broadcast łapie też podmianę waluty selectem w podsumowaniu
  // i programowe zmiany (domyślna waluta działu), które nie emitują "change" na polu waluty faktury.
  invoiceOutletConnected() {
    this.syncExchangeCurrencyOptions()
  }

  invoiceCurrencyChanged() {
    this.handleExchangeCurrency()
  }

  // currency#handleMoreCurrencies emituje `update` także po rozwinięciu „więcej…", po którym select
  // zwykle zostaje na tej samej walucie — bez porównania każde rozwinięcie listy kasowałoby podgląd
  // kursu i pytało serwer od nowa.
  exchangeCurrencySettled() {
    if (this.exchangeCurrencyTarget.value === this.handledExchangeCurrency) return

    this.handleExchangeCurrency()
  }

  handleExchangeCurrency() {
    this.syncExchangeCurrencyOptions()
    this.handledExchangeCurrency = this.exchangeCurrencyTarget.value
    const hasExchangeCurrency = this.exchangeCurrencyTarget.value !== ""

    this.toggleHidden(this.exchangeKindWrapperTarget, !hasExchangeCurrency)
    this.toggleHidden(this.exchangeRateWrapperTarget, !hasExchangeCurrency)

    if (!hasExchangeCurrency) {
      this.toggleHidden(this.exchangeNoteWrapperTarget, true)
      this.exchangeCurrencyRateTarget.value = ""
      this.exchangeNoteTarget.value = ""
      // Checkbox "pokaż przeliczenia na wizualizacji" zależy od pary walut — bez notyfikacji
      // zostałby zaznaczony i wysłał "1" dla faktury bez przeliczenia.
      this.currencySelectionChanged()
      return
    }

    this.clearExchangeKindOptionsPreviews()
    // Przy kindzie "own" zmiana waluty nie może nadpisać wpisanych przez usera wartości
    // kursu/notatki — ale musi odblokować pole kursu (spójnie z wersją bootstrapową).
    if (this.exchangeKindTarget.value === "own") {
      this.applyOwnModeLayout(true)
    } else {
      this.applyExchangeKindPreview()
    }
    this.currencySelectionChanged()
  }

  handleExchangeKindChange() {
    this.applyExchangeKindPreview()
    this.currencySelectionChanged()
  }

  // Data kursu to min(data sprzedaży, data wystawienia), a na UA data zapłaty — ręczna edycja
  // któregokolwiek z tych pól zmienia kurs, który policzy zapis, więc formatka musi pokazać nowy.
  // Pola dat leżą poza sekcją kursu (osobny partial), stąd nasłuch na dokumencie i filtr po
  // targetach outletu invoice.
  exchangeDateChanged(event) {
    if (!EXCHANGE_DATE_TARGETS.some((name) => this.outletFieldElement(name) === event.target)) return

    clearTimeout(this.previewRefreshTimer)
    this.previewRefreshTimer = setTimeout(() => this.refreshPreviewForDates(), PREVIEW_REFRESH_DELAY_MS)
  }

  // Kurs własny nie pochodzi z banku, a sentinela "więcej..." serwer nie zna — obie ścieżki
  // zostawiają pole takim, jakie jest.
  refreshPreviewForDates() {
    const exchangeCurrency = this.exchangeCurrencyTarget.value
    if (this.exchangeKindTarget.value === "own" || exchangeCurrency === "" || exchangeCurrency === "more") return

    this.clearExchangeKindOptionsPreviews()
    this.fetchRemotePreview()
  }

  // Serwer wyłącza opcję równą walucie faktury z renderu; po zmianie waluty głównej przenosimy to
  // wyłączenie, żeby np. PLN wrócił na listę na fakturze przełączonej z PLN na EUR. Waluta, która
  // przestała być dopuszczalna, nie może zostać wybrana — przeliczenie EUR→EUR nie istnieje.
  syncExchangeCurrencyOptions() {
    const invoiceCurrency = this.invoiceCurrency
    if (!invoiceCurrency) return

    let selectionBlocked = false
    for (const option of this.exchangeCurrencyTarget.options) {
      if (!option.value || option.value === "more") continue

      const blocked = option.value === invoiceCurrency
      option.hidden = blocked
      option.disabled = blocked
      if (blocked && option.selected) selectionBlocked = true
    }
    if (selectionBlocked) this.exchangeCurrencyTarget.value = ""
  }

  // Kurs to jedna liczba — poza cyframi zostawiamy tylko OSTATNI separator (dziesiętny), wcześniejsze
  // traktujemy jak separatory tysięcy. Inaczej "1.234,56" doszłoby do backendu jako 1,23456, a setter
  // Invoice#exchange_currency_rate= (tr "," → ".") i tak nie odróżnia tysięcy od części dziesiętnej.
  sanitizeRate() {
    const input = this.exchangeCurrencyRateTarget
    const digitsAndSeparators = input.value.replace(/[^0-9.,]/g, "")
    const lastSeparator = digitsAndSeparators.search(/[.,](?=[^.,]*$)/)
    const cleaned = lastSeparator === -1
      ? digitsAndSeparators
      : digitsAndSeparators.slice(0, lastSeparator).replace(/[.,]/g, "") +
        digitsAndSeparators.slice(lastSeparator)

    if (input.value !== cleaned) input.value = cleaned
  }

  applyExchangeKindPreview() {
    const kindSelect = this.exchangeKindTarget
    const selectedOption = kindSelect.selectedOptions[0]
    const isOwn = kindSelect.value === "own"
    const rate = selectedOption?.dataset.rate || ""
    const rateDisplay = selectedOption?.dataset.rateDisplay || rate
    const note = selectedOption?.dataset.note || ""

    this.exchangeCurrencyRateTarget.value = isOwn ? rate : rateDisplay
    this.exchangeNoteTarget.value = note
    this.applyOwnModeLayout(isOwn)

    if (!isOwn && !rateDisplay) this.fetchRemotePreview()
  }

  // Render wkłada preview w data-* tylko dla waluty przeliczenia wybranej w tym momencie; każdą inną
  // parę (zmiana waluty, wybór z "więcej...") liczy serwer — jak wariant bootstrapowy.
  async fetchRemotePreview() {
    const exchangeCurrency = this.exchangeCurrencyTarget.value
    const kind = this.exchangeKindTarget.value
    if (!this.hasPreviewUrlValue || !kind || !exchangeCurrency) return

    const selection = this.previewSelection
    const cached = this.previewCache.get(selection)
    if (cached) return this.applyRemotePreview(cached, selection)

    const params = new URLSearchParams({
      exchange_currency: exchangeCurrency,
      exchange_kind: kind,
      kind: this.invoiceKindValue,
      base_invoice_id: this.baseInvoiceIdValue,
      ...this.invoiceDateAndCurrencyParams,
    })

    // URL zamiast konkatenacji — previewUrlValue może już nieść query string (np. deweloperskie
    // ?view_variant=tailwind propagowane przez default_url_options), a drugie "?" zjadałoby parametry.
    const url = new URL(this.previewUrlValue, window.location.origin)
    for (const [key, value] of params) url.searchParams.set(key, value)

    let preview
    try {
      const response = await fetch(url, { headers: { Accept: "application/json" } })
      preview = response.ok ? await response.json() : { error: `http_${response.status}` }
    } catch {
      preview = { error: "fetch_failed" }
    }

    // Cache tylko sukcesów — przejściowy brak odpowiedzi nie może przykleić się do waluty na całą sesję formatki.
    if (!preview.error) this.previewCache.set(selection, preview)
    this.applyRemotePreview(preview, selection)
  }

  applyRemotePreview(preview, selection) {
    // Odpowiedź mogła przyjść po kolejnej zmianie waluty albo kindu — wtedy dotyczy już czegoś innego.
    if (selection !== this.previewSelection) return

    if (!preview.error) {
      this.exchangeCurrencyRateTarget.value = preview.display
      return
    }

    // "Brak kursu" to odpowiedź serwera o danych; zerwane połączenie i błąd 5xx to awaria pobrania —
    // user musi wiedzieć, że warto ponowić, zamiast szukać kursu, którego rzekomo nie ma.
    const fetchFailed = preview.error === "fetch_failed" || preview.error.startsWith("http_")
    this.exchangeCurrencyRateTarget.value = fetchFailed ? this.rateFetchFailedValue : this.rateUnavailableValue
  }

  // Klucz cache'u i guard na wyścig składamy z tych samych wartości, które lecą w zapytaniu —
  // kurs zależy nie tylko od pary walut i banku, ale i od dat (data kursu to min(data sprzedaży,
  // data wystawienia), na UA data zapłaty). Klucz zbudowany z węższego zestawu oddawałby
  // z cache'u kurs policzony dla innego dnia.
  get previewSelection() {
    return Object.values(this.invoiceDateAndCurrencyParams)
      .concat(this.exchangeCurrencyTarget.value, this.exchangeKindTarget.value).join("|")
  }

  get invoiceCurrency() {
    return this.outletFieldValue("currency")
  }

  get invoiceDateAndCurrencyParams() {
    return {
      currency: this.invoiceCurrency,
      issue_date: this.outletFieldValue("issueDate"),
      transaction_date: this.outletFieldValue("sellDate"),
      paid_date: this.outletFieldValue("paidDate"),
    }
  }

  // Nie każdy wariant formatki renderuje wszystkie pola (np. data sprzedaży bywa ukryta,
  // data zapłaty liczy się tylko na UA) — brak targetu oznacza brak wartości, nie błąd.
  outletFieldValue(targetName) {
    return this.outletFieldElement(targetName)?.value || ""
  }

  outletFieldElement(targetName) {
    if (!this.hasInvoiceOutlet) return null

    const capitalized = targetName[0].toUpperCase() + targetName.slice(1)
    return this.invoiceOutlet[`has${capitalized}Target`] ? this.invoiceOutlet[`${targetName}Target`] : null
  }

  applyOwnModeLayout(isOwn) {
    this.exchangeCurrencyRateTarget.readOnly = !isOwn
    this.exchangeCurrencyRateTarget.disabled = !isOwn
    this.toggleHidden(this.exchangeNoteWrapperTarget, !isOwn)
    this.setRateRequired(isOwn)
  }

  // Gwiazdka required przy labelu — atrybut data-required + warianty Tailwind (data-[required=true]:after:*)
  // w HTML. Zero klas CSS w JS.
  setRateRequired(isOwn) {
    if (!this.hasExchangeCurrencyRateLabelTarget) return
    this.exchangeCurrencyRateLabelTarget.dataset.required = String(isOwn)
  }

  // Preview kursu siedzi w data-* opcji kindu (przełączenie banku bez round-tripa), ale dotyczy pary
  // walut z momentu renderu. Po zmianie którejkolwiek z walut te wartości są nieaktualne, więc je
  // czyścimy — applyExchangeKindPreview widzi pusty kurs i dociąga go z serwera. Opcję "own"
  // pomijamy: nosi wartości wpisane przez użytkownika.
  clearExchangeKindOptionsPreviews() {
    for (const option of this.exchangeKindTarget.options) {
      if (option.value === "own") continue

      option.dataset.rate = ""
      option.dataset.rateDisplay = ""
      option.dataset.note = ""
      option.dataset.date = ""
    }
  }

  toggleHidden(element, hidden) {
    element.classList.toggle("hidden", hidden)
  }

  // Odpowiednik serwerowego Invoice#exchange_display_checkbox_available?: kierunek liczony
  // z obu walut, bramka locale/created_at przekazana z serwera w data-era-available.
  updateExchangeDisplayCheckbox() {
    if (!this.hasExchangeDisplayWrapperTarget) return

    const currency = this.invoiceCurrency
    const exchangeCurrency = this.exchangeCurrencyTarget.value
    const eraAvailable = this.exchangeDisplayWrapperTarget.dataset.eraAvailable === "true"
    const available = eraAvailable && exchangeCurrency !== "" && exchangeCurrency !== currency &&
      (currency === "PLN" || exchangeCurrency === "PLN")

    // Samo schowanie nie wystarczy — zaznaczony, ukryty checkbox dalej POST-uje "1"
    // i flaga odżyłaby po późniejszym dodaniu waluty przeliczenia.
    if (!available) this.exchangeDisplayCheckboxTarget.checked = false
    this.exchangeDisplayWrapperTarget.classList.toggle("hidden", !available)
  }

  notifyCurrencyNotInBank() {
    const kindUpper = this.exchangeKindTarget.value.toUpperCase()
    const currency = this.invoiceCurrency
    if (kindUpper === "OWN" || this.exchangeCurrencyTarget.value === "" || currency === this.accountCurrencyValue) return

    const availableCurrencies = this.exchangeRatesAvailableForBanksValue[kindUpper]
    if (availableCurrencies == null || availableCurrencies.includes(currency.toUpperCase())) return

    const bankLabel = this.exchangeKindTarget.selectedOptions[0]?.innerText || ""
    showNotification(this.currencyNotInBankValue, true, ["%{bank}"], [bankLabel])
  }

  currencySelectionChanged() {
    this.notifyCurrencyNotInBank()
    this.updateExchangeDisplayCheckbox()
  }
}
